import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  SourcingEventStatus,
  useCreateNegotiationRoundMutation,
  useReopenCompletedSourcingEventMutation,
  type NegotiationRoundDto,
  type SourcingEventDto,
} from "../../../autogen/bff-api";
import { useApiError } from "../../../common/errors/useApiError";
import { urls } from "../../../urls";
import { JustificationModal } from "./JustificationModal";
import { draftRoundOf, startingDialogueDepartsFromPlan } from "./partIIDialogue";
import { useRoundSelection } from "./RoundSelectionContext";

const DEFAULT_ROUND_DAYS = 14;

export const useStartRound = (event: SourcingEventDto): { start: () => void; modal: ReactNode; isStarting: boolean } => {
  const { t } = useTranslation();
  const { trigger } = useApiError();
  const navigate = useNavigate();
  const { rounds, selectRound } = useRoundSelection();
  const [createRound, { isLoading: isCreating }] = useCreateNegotiationRoundMutation();
  const [reopenEvaluation, { isLoading: isReopening }] = useReopenCompletedSourcingEventMutation();
  const isLoading = isCreating || isReopening;
  const [asksForJustification, setAsksForJustification] = useState(false);

  const reopenCompletedEvaluation = async () => {
    if (event.status === SourcingEventStatus.Evaluated) await reopenEvaluation({ eventId: event.id }).unwrap();
  };

  const create = async (dialogueDepartureJustification?: string) => {
    const stageDeadlines = [event.eventData.bidSubmissionDeadline, ...rounds.map((round) => round.deadline)]
      .filter((iso): iso is string => iso != null)
      .map((iso) => new Date(iso).getTime());
    const roundDeadline = new Date(Math.max(Date.now(), ...stageDeadlines));
    roundDeadline.setDate(roundDeadline.getDate() + DEFAULT_ROUND_DAYS);
    roundDeadline.setSeconds(0, 0);
    try {
      await reopenCompletedEvaluation();
      const newRound = await createRound({
        eventId: event.id,
        createNegotiationRoundRequest: {
          deadline: roundDeadline.toISOString(),
          isFinalRound: null,
          dialogueDepartureJustification,
        },
      }).unwrap();
      setAsksForJustification(false);
      selectRound(newRound);
      navigate(urls.app.sourcingEvents.editNegotiationRound.go(event.id, newRound.id));
    } catch (e) {
      trigger(e);
    }
  };

  const openDraft = async (draft: NegotiationRoundDto) => {
    try {
      await reopenCompletedEvaluation();
      selectRound(draft);
      navigate(urls.app.sourcingEvents.editNegotiationRound.go(event.id, draft.id));
    } catch (e) {
      trigger(e);
    }
  };

  const start = () => {
    const draft = draftRoundOf(rounds);
    if (draft != null) {
      void openDraft(draft);
      return;
    }
    if (startingDialogueDepartsFromPlan(event, rounds)) {
      setAsksForJustification(true);
      return;
    }
    void create();
  };

  const modal = (
    <JustificationModal
      isOpen={asksForJustification}
      title={t("Hold dialogue that was not planned")}
      description={t(
        "The procurement documents said no dialogue was planned. Explain why you hold it anyway (FOA § 10-5 (2) h).",
      )}
      label={t("Why dialogue is held after all")}
      confirmLabel={t("Start the dialogue")}
      isLoading={isLoading}
      onConfirm={(justification) => void create(justification)}
      onClose={() => setAsksForJustification(false)}
    />
  );

  return { start, modal, isStarting: isLoading };
};
